import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { getFormToFill, submitFormResponse } from '../../api';
import { usePlayerData } from './PlayerDataContext';

const ACCENT = '#3b82f6';

function ScaleGrid({ min, max, value, onChange }) {
  const nums = [];
  for (let n = min; n <= max; n++) nums.push(n);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
      {nums.map(n => {
        const selected = value === n;
        return (
          <button key={n} onClick={() => onChange(n === value ? null : n)}
            style={{
              width: 56, height: 56, borderRadius: 14, fontFamily: 'inherit',
              border: `2px solid ${selected ? ACCENT : 'var(--border-strong)'}`,
              background: selected ? ACCENT : 'var(--bg-card)',
              color: selected ? '#fff' : 'var(--text-secondary)',
              fontSize: 20, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
            }}>{n}</button>
        );
      })}
    </div>
  );
}

function ChoiceButtons({ options, value, multi, onChange }) {
  const selected = multi ? (value || []) : value;
  const isSelected = (opt) => multi ? selected.includes(opt) : selected === opt;
  const toggle = (opt) => {
    if (multi) {
      const cur = value || [];
      onChange(cur.includes(opt) ? cur.filter(o => o !== opt) : [...cur, opt]);
    } else {
      onChange(opt === value ? null : opt);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {options.map(opt => (
        <button key={opt} onClick={() => toggle(opt)}
          style={{
            padding: '14px 16px', borderRadius: 12, textAlign: 'left', fontFamily: 'inherit',
            border: `2px solid ${isSelected(opt) ? ACCENT : 'var(--border-strong)'}`,
            background: isSelected(opt) ? 'rgba(59,130,246,0.1)' : 'var(--bg-card)',
            color: isSelected(opt) ? ACCENT : 'var(--text-primary)',
            fontSize: 15, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
          }}>{opt}</button>
      ))}
    </div>
  );
}

export default function FormFillWizard({ playerRecord }) {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { refresh } = usePlayerData();

  const [form, setForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!playerRecord?.id) return;
    getFormToFill(formId, playerRecord.id)
      .then(({ form, questions }) => { setForm(form); setQuestions(questions); })
      .catch(() => setError('This form is no longer available.'))
      .finally(() => setLoading(false));
  }, [formId, playerRecord?.id]);

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: 'var(--text-muted)' }}>Loading…</div>;
  }

  if (error || !form) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 16, padding: 32, textAlign: 'center', background: 'var(--bg-app)' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>{error || 'Form not found.'}</p>
        <button onClick={() => navigate('/player/forms')}
          style={{ padding: '12px 28px', borderRadius: 12, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Back to Forms
        </button>
      </div>
    );
  }

  const total = questions.length;
  const q = questions[step];
  const value = answers[q.id];
  const setValue = (v) => setAnswers(a => ({ ...a, [q.id]: v }));
  const isLast = step === total - 1;
  const canProceed = !q.required || (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0));

  const handleNext = async () => {
    if (!isLast) {
      setStep(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        player_id: parseInt(playerRecord.id),
        answers: questions
          .filter(qq => answers[qq.id] !== undefined && answers[qq.id] !== null && answers[qq.id] !== '')
          .map(qq => ({ question_id: qq.id, value: answers[qq.id] })),
      };
      await submitFormResponse(formId, payload);
      await refresh();
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 20, padding: 32, textAlign: 'center', background: 'var(--bg-app)' }}>
        <CheckCircle size={72} color="#10b981" strokeWidth={1.5} />
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#10b981', margin: 0 }}>All done!</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, margin: 0 }}>Your response to "{form.title}" has been submitted.</p>
        <button onClick={() => navigate('/player/forms')}
          style={{ marginTop: 12, padding: '14px 32px', borderRadius: 12, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ padding: '16px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <button onClick={() => step === 0 ? navigate('/player/forms') : setStep(s => s - 1)}
            style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: 0, fontFamily: 'inherit' }}>
            ← {step === 0 ? 'Back' : 'Previous'}
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Question {step + 1} of {total}</span>
        </div>
        <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
          <div style={{ height: 4, width: `${((step + 1) / total) * 100}%`, background: ACCENT, borderRadius: 2, transition: 'width 0.3s ease' }} />
        </div>
      </div>

      <div style={{ padding: '28px 24px 140px', maxWidth: 480, margin: '0 auto' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4, lineHeight: 1.3 }}>{form.title}</h2>
        <p style={{ fontSize: 12, color: 'var(--text-dimmed)', marginBottom: 24 }}>{form.description}</p>

        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, lineHeight: 1.3 }}>
          {q.question_text}{!q.required && <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-dimmed)' }}> (optional)</span>}
        </h3>

        {q.type === 'text' && (
          <textarea value={value || ''} onChange={e => setValue(e.target.value)}
            style={{ width: '100%', minHeight: 120, padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 15, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
            placeholder="Type your answer…" />
        )}

        {q.type === 'scale' && (
          <>
            <ScaleGrid min={q.scale_min ?? 1} max={q.scale_max ?? 10} value={value} onChange={setValue} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-dimmed)', marginTop: 14 }}>
              <span>{q.scale_min_label || q.scale_min}</span>
              <span>{q.scale_max_label || q.scale_max}</span>
            </div>
          </>
        )}

        {q.type === 'multiple_choice' && (
          <ChoiceButtons options={q.options || []} value={value} multi={false} onChange={setValue} />
        )}

        {q.type === 'checkbox' && (
          <ChoiceButtons options={q.options || []} value={value} multi onChange={setValue} />
        )}

        {q.type === 'yesno' && (
          <div style={{ display: 'flex', gap: 12 }}>
            {['yes', 'no'].map(opt => (
              <button key={opt} onClick={() => setValue(opt === value ? null : opt)}
                style={{
                  flex: 1, height: 64, borderRadius: 14, textTransform: 'capitalize', fontFamily: 'inherit',
                  border: `2px solid ${value === opt ? ACCENT : 'var(--border-strong)'}`,
                  background: value === opt ? ACCENT : 'var(--bg-card)',
                  color: value === opt ? '#fff' : 'var(--text-secondary)',
                  fontSize: 16, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                }}>{opt}</button>
            ))}
          </div>
        )}

        {error && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 16 }}>{error}</p>}
      </div>

      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px 24px max(16px, env(safe-area-inset-bottom))', background: 'var(--bg-app)', borderTop: '1px solid var(--border)' }}>
        <button onClick={handleNext} disabled={saving || !canProceed}
          style={{
            width: '100%', height: 52, borderRadius: 12, border: 'none',
            background: isLast ? '#10b981' : ACCENT, color: '#fff', fontSize: 16, fontWeight: 700,
            cursor: (!canProceed || saving) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            opacity: (!canProceed || saving) ? 0.5 : 1,
          }}>
          {saving ? 'Submitting…' : isLast ? '✓ Submit' : 'Next →'}
        </button>
      </div>
    </div>
  );
}
